import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-receipt-template',
  templateUrl: './receipt-template.component.html',
  styleUrls: ['./receipt-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ CommonModule]
})
export class ReceiptTemplateComponent {
  @Input() receiptData: any;

  get receiptTitle(): string {
    const transactionType = this.receiptData?.transactionType?.trim() || '';
    
    if (transactionType.toLowerCase().includes('credit')) {
      return 'CREDIT RECEIPT';
    } else if (transactionType.toLowerCase().includes('refund')) {
      return 'REFUND RECEIPT';
    } else {
      return 'PAYMENT CONFIRMATION';
    }
  }

  /**
   * Get grouped breakdown items for display
   * Groups items by description (only duplicates), preserves order, keeps negative amounts at end
   */
  get processedBreakdownItems(): any[] {
    if (!this.receiptData?.breakdownItems || this.receiptData.breakdownItems.length === 0) {
      return [];
    }

    // Group items by revenue_code_desc, preserving first occurrence order
    const groupedMap = new Map<string, any>();
    const orderMap = new Map<string, number>(); // Track first occurrence order

    this.receiptData.breakdownItems.forEach((item: any, index: number) => {
      // Use revenue_code_desc if available, otherwise use dynamic description based on transaction type
      let desc = item.revenue_code_desc?.trim();
      if (!desc) {
        desc = this.getDefaultDescription();
      }
      
      const price = parseFloat(item.price || 0);
      const quantity = parseInt(item.quantity || 1, 10);
      const paidAmount = parseFloat(item.paid_amount || 0);

      if (groupedMap.has(desc)) {
        // Item exists - sum quantity and paid_amount
        const existing = groupedMap.get(desc);
        existing.quantity += quantity;
        existing.paid_amount += paidAmount;
      } else {
        // First occurrence - store order and create entry
        orderMap.set(desc, index);
        groupedMap.set(desc, {
          revenue_code_desc: desc,
          quantity: quantity,
          price: price,
          paid_amount: paidAmount,
          isNegative: paidAmount < 0
        });
      }
    });

    // Convert map to array
    const groupedItems = Array.from(groupedMap.values());

    // Sort: positive amounts first (in original order), then negative amounts (in original order)
    return groupedItems.sort((a, b) => {
      // Separate positive and negative amounts
      if (!a.isNegative && b.isNegative) return -1;
      if (a.isNegative && !b.isNegative) return 1;

      // Within same category, maintain original order
      const orderA = orderMap.get(a.revenue_code_desc) || 0;
      const orderB = orderMap.get(b.revenue_code_desc) || 0;
      return orderA - orderB;
    });
  }

  /**
   * Calculate total amount from all breakdown items from API
   */
  get totalAmount(): string {
    if (this.receiptData?.breakdownItems && this.receiptData.breakdownItems.length > 0) {
      const total = this.receiptData.breakdownItems.reduce((sum: number, item: any) => {
        const amount = parseFloat(item.paid_amount || 0);
        return sum + amount;
      }, 0);
      // Return whole number without decimals
      return Math.round(total).toString();
    }
    // Fallback to paymentAmount if no breakdown items
    const paymentAmount = this.receiptData?.paymentAmount || '0';
    const cleaned = paymentAmount.toString().replace('$', '');
    const num = parseFloat(cleaned);
    return Math.round(num).toString();
  }

  /**
   * Format amount (remove negative sign for display, no decimals)
   */
  formatAmount(amount: any): string {
    const numAmount = parseFloat(amount || 0);
    return Math.abs(Math.round(numAmount)).toString();
  }

  /**
   * Check if amount is negative (for credits/exemptions)
   */
  isNegativeAmount(amount: any): boolean {
    return parseFloat(amount || 0) < 0;
  }

  /**
   * Get default description based on transaction type
   */
  getDefaultDescription(): string {
    const transactionType = this.receiptData?.transactionType?.trim()?.toLowerCase() || '';
    
    if (transactionType.includes('credit')) {
      return 'Credit';
    } else if (transactionType.includes('refund')) {
      return 'Refund';
    } else {
      return 'Application Fees';
    }
  }
}
